/**
 * Factory state for the CheckBuilder studio.
 *
 * This module is the SINGLE source of truth for the out-of-box state
 * a brand-new visitor gets: CheckBuilder's useState initializers are
 * seeded from these builders. It serves TWO reset consumers, and both
 * derive from the one definition below so they can never drift:
 *
 *  - "Start a blank check" (an explicit control of its own) applies
 *    the FULL factory snapshot: blank data, logo removed, positions
 *    and deletions cleared, back design-matched. The check being
 *    left is pinned as the previous check first, so it stays
 *    recoverable from the start options on the next visit.
 *  - "Default Clean Style" applies only the DESIGN slice of the
 *    factory snapshot (buildDesignOnlyResetSnapshot): the design
 *    returns to the clean factory design while everything the user
 *    typed or placed - checkData, logo, signature, custom objects,
 *    positions, deletions - survives untouched (Princewill's ruling,
 *    2026-11-04: "Keep my typed details when I tap Default Clean
 *    Style." This supersedes the earlier same-day ruling that made
 *    Default Clean a full wipe; the full clear lives on the
 *    blank-start control now.)
 *
 * The factory check is BLANK (Princewill, 2026-11-04): fully arranged
 * like a real bank check - every line, label, box, border, security
 * default and the matched Classic Personal back in place - but with
 * NO write-ups: no sample names, amounts or numbers pre-filled, so a
 * user never has to edit out content they did not write. The studio
 * lands on this blank check; a previous check is offered as an
 * explicit start option, never silently restored (see
 * useHistorySnapshots' deferRestore mode and isBlankCheckSnapshot
 * below).
 *
 * The snapshot shape is exactly the shape useHistorySnapshots
 * persists/restores for the check editor, so both resets flow
 * through the same apply() as every other state and the
 * working-state machinery persists them - a refresh after a reset
 * shows the reset state, and nothing cleared can resurrect.
 */

export const FACTORY_SECURITY_FEATURES = [
    { id: 'microprint', name: 'Microprint Border', enabled: true },
    { id: 'guilloche', name: 'Guilloche Pattern', enabled: true },
    { id: 'void_pantograph', name: 'VOID Pantograph', enabled: true },
    { id: 'watermark', name: 'Watermark', enabled: true },
    { id: 'uv_markers', name: 'UV Security Markers', enabled: false },
    { id: 'holographic', name: 'Holographic Strip', enabled: false },
    { id: 'chemical_reactive', name: 'Chemical Reactive Paper', enabled: true },
    { id: 'thermochromic', name: 'Thermochromic Ink', enabled: true },
    { id: 'warning_band', name: 'Security Warning Band', enabled: true },
    { id: 'anti_copy_vdot', name: 'Anti-Copy V-Dot', enabled: true },
    { id: 'prismatic_column', name: 'Prismatic Security Column', enabled: true },
    { id: 'microprint_sig', name: 'Microprint Signature Line', enabled: true },
    { id: 'cpsa_padlock', name: 'CPSA Padlock Icon', enabled: true },
    { id: 'micr_clear_band', name: 'MICR Clear Band Protection', enabled: true }
];

/** Fresh copies each call - state must never share the module array. */
export const factorySecurityFeatures = (): typeof FACTORY_SECURITY_FEATURES =>
    FACTORY_SECURITY_FEATURES.map(f => ({ ...f }));

/**
 * The initial checkData a brand-new visitor gets: BLANK. Every
 * write-up field is empty - the user fills in their own check.
 * Nothing written = nothing printed (the same rule the blank-amount
 * rendering already follows): the MICR line prints nothing until
 * numbers are entered, and Bank Acceptance honestly flags the empty
 * fields. Only arrangement/design defaults carry values (font sizes,
 * the watermark security text, the MICR typeface).
 */
export const factoryCheckData = () => ({
    checkNumber: '',
    fractionalRouting: '',
    date: '',
    amount: 0,
    payeeName: '',
    writtenAmount: '',
    bankName: '',
    memo: '',
    routingNumber: '',
    accountNumber: '',
    accountHolderName: '',
    accountHolderAddress: '',
    bankAddress: '',
    watermarkText: 'AUTHENTIC DOCUMENT',
    payeeFontSize: 18,
    memoFontSize: 13,
    aiPrompt: '', // Start with empty prompt
    isAiActive: false,
    micrFont: 'e13b',
    micrFontSize: 22,
    epcDigit: '', // Optional External Processing Code
    ein: '',
    lastFour: '', // Last 4 of card for filename
    hoursWorked: '',
    payRate: '',
    payTotal: '',
    ytdGross: '0,000.00',
    ytdNet: '0,000.00',
    payeeAddress: ''
});

/**
 * Is this check snapshot BLANK - the factory content, or so close to
 * it that there is no user work worth offering as a "previous check"?
 * The test is semantic, not a deep-equal against the factory object:
 * every write-up field empty, no amount, no placed logo or signature,
 * no custom objects. Design-only differences (a style, paper or paper
 * colour picked before typing anything) still count as blank - there
 * is no content to resume. A date alone also counts as blank.
 * useHistorySnapshots' deferRestore mode uses this to decide whether
 * a stored state is a previous check to offer at start.
 */
export const isBlankCheckSnapshot = (snap: any): boolean => {
    if (!snap || typeof snap !== 'object') return true;
    const d = snap.checkData || {};
    const nonEmpty = (v: any) => v != null && String(v).trim().length > 0;
    const writeUps = [
        d.payeeName, d.bankName, d.bankAddress, d.accountHolderName,
        d.accountHolderAddress, d.routingNumber, d.accountNumber,
        d.checkNumber, d.fractionalRouting, d.memo, d.writtenAmount,
        d.payeeAddress
    ];
    if (writeUps.some(nonEmpty)) return false;
    const amount = typeof d.amount === 'number' ? d.amount : parseFloat(String(d.amount ?? ''));
    if (!Number.isNaN(amount) && amount !== 0) return false;
    if (snap.logoImage || snap.signatureImage) return false;
    if (Array.isArray(snap.placedLogos) && snap.placedLogos.length > 0) return false;
    if (Array.isArray(snap.customObjects) && snap.customObjects.length > 0) return false;
    return true;
};

/** Factory medallion: ash grey, flat — the disc on the owner's
 *  reference check. Colour/finish are per-design state (sealDesign). */
export const FACTORY_SEAL_COLOR = '#a7adb5';
export const FACTORY_SEAL_FINISH = 'flat';

export const factoryBorderConfig = () => ({
    style: 'BRAIDED' as const,
    color: '#003366',
    thickness: 3,
    cornerRadius: 0
});

/**
 * The complete factory snapshot, in the exact shape of CheckBuilder's
 * history snapshot: every design value back to its out-of-box
 * default, the logo removed with its options reset, positions and
 * deletions cleared (front and back), and the back untouched by the
 * user (backStyleTouched false, so the design-matched variant for
 * the clean design governs - a separately saved default back is a
 * stored preference and is NOT deleted; it simply does not apply to
 * this state until the next design switch, per its existing rule).
 * This is the state a brand-new visitor gets AND the state the
 * explicit "Start a blank check" control clears to.
 */
export const buildFactoryCheckSnapshot = () => ({
    checkData: factoryCheckData(),
    selectedBank: '',
    paperColor: '#f8f4ec',
    selectedTexture: 'linen',
    selectedFont: 'times',
    checkStyle: 'personal',
    // Matches DEFAULT_BUSINESS_WIDTH / DEFAULT_BUSINESS_HEIGHT in
    // CheckBuilder (850 x 350 - 8.5" x 3.5" at 100 DPI).
    checkDimensions: { width: 850, height: 350 },
    borderConfig: factoryBorderConfig(),
    securityFeatures: factorySecurityFeatures(),
    sealColor: FACTORY_SEAL_COLOR,
    sealFinish: FACTORY_SEAL_FINISH,
    objectPositions: {},
    deletedIds: [],
    deletedBackIds: [],
    backSideStyle: 'mobile',
    backStyleTouched: false,
    signatureStyle: 'ballpoint',
    handwritingStyle: 'cursive',
    handwritingColor: '#1b2440',
    handwritingRotation: -1.2,
    handwritingVariation: 0.5,
    textureOpacity: 1.0,
    showMicrBand: true,
    showAmountBorder: true,
    customObjects: [],
    placedLogos: [],
    logoImage: null,
    signatureImage: null,
    rawLogoImage: null,
    logoRemoveBackground: true,
    logoIncludeWriteups: true,
    logoCropPercent: null,
    logoDetectedSplit: null,
    logoFileName: null,
    activeLogoId: null,
    activeSignatureId: null,
    designPattern: null,
    fractionalAuto: true
});

/**
 * The DESIGN slice of the factory snapshot: the values that are a
 * design choice rather than user-entered content - template/style,
 * dimensions, paper/texture/font, border, the security-feature set,
 * the medallion (seal colour/finish), texture opacity, the MICR
 * toggles, the imported design pattern (it functions as the paper
 * design when applied, so a paper reset includes clearing it), and
 * the back override state (backStyleTouched false: the back returns
 * to the design-matched variant for the clean design, exactly as a
 * normal design switch behaves - a separately saved "my default
 * back" preference is untouched and re-applies on the next switch).
 *
 * Derived from buildFactoryCheckSnapshot() itself, so the design
 * defaults exist in exactly one place: whatever the factory design
 * is, this slice is.
 */
export const factoryDesignSlice = () => {
    const s = buildFactoryCheckSnapshot();
    return {
        selectedBank: s.selectedBank,
        checkStyle: s.checkStyle,
        checkDimensions: s.checkDimensions,
        paperColor: s.paperColor,
        selectedTexture: s.selectedTexture,
        selectedFont: s.selectedFont,
        borderConfig: s.borderConfig,
        securityFeatures: s.securityFeatures,
        sealColor: s.sealColor,
        sealFinish: s.sealFinish,
        textureOpacity: s.textureOpacity,
        showMicrBand: s.showMicrBand,
        showAmountBorder: s.showAmountBorder,
        backSideStyle: s.backSideStyle,
        backStyleTouched: s.backStyleTouched,
        designPattern: s.designPattern
    };
};

/**
 * "Default Clean Style" (Princewill's ruling, 2026-11-04): a
 * DESIGN-ONLY reset. The factory design slice is laid over the
 * user's current snapshot; everything NOT in the slice survives
 * exactly as the user left it:
 *  - the full checkData (holder/bank/payee names and addresses,
 *    amount + written amount, routing/account/check numbers,
 *    fractional routing + fractionalAuto, memo, date, EIN - every
 *    field),
 *  - the placed logo AND its options (remove-background, write-ups,
 *    crop) AND its filename label state, plus the raw upload,
 *  - signature and handwriting (image, style, colour, rotation,
 *    variation),
 *  - custom objects,
 *  - the user's arranged positions and deletions, front AND back.
 */
export const buildDesignOnlyResetSnapshot = (current: any) => ({
    ...(current ?? {}),
    ...factoryDesignSlice()
});
